import { useEffect } from "react";
import * as THREE from "three";

const GradientWave = () => {
  useEffect(() => {
    const canvas = document.getElementById("bg");

    if (!canvas) {
      console.error("Canvas element with id 'bg' not found.");
      return;
    }

    const scene = new THREE.Scene();
    scene.background = null; // Pas de fond

    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.set(0, 20, 40);

    const renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      alpha: true, // Garder le fond transparent
      antialias: false,
    });
    renderer.setPixelRatio(window.devicePixelRatio * 0.8);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0); // Fond transparent

    const resizeRenderer = () => {
      renderer.setSize(window.innerWidth, window.innerHeight);
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
    };

    window.addEventListener("resize", resizeRenderer);
    resizeRenderer();

    // Lumière ambiante plus forte pour éclairer davantage les objets
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.5); // Lumière ambiante plus forte
    scene.add(ambientLight);

    // Lumière directionnelle ajustée
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1); // Lumière directionnelle modérée
    directionalLight.position.set(10, 20, 10);
    scene.add(directionalLight);

    // Ajout d'une lumière ponctuelle pour l'effet de lueur
    const pointLight = new THREE.PointLight(0x00aaff, 1, 100); // Lueur bleue
    pointLight.position.set(0, 15, 0); // Position de la lumière
    scene.add(pointLight);

    const particles = [];
    const particleGroup = new THREE.Group();
    scene.add(particleGroup);

    const maxDis = 50;
    const pointSize = 0.8;
    const spacing = 2.5;
    const maxHeight = 12;

    // Création des particules
    for (let x = -maxDis; x <= maxDis; x += spacing) {
      for (let z = -maxDis; z <= maxDis; z += spacing) {
        const y = Math.sin(Math.sqrt(x ** 2 + z ** 2) / 8) * 8;

        const geometry = new THREE.SphereGeometry(pointSize, 32, 32);
        const material = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          emissive: 0x00aaff, // Lueur bleue
          emissiveIntensity: 1.0, // Augmentation de l'intensité de la lueur
          roughness: 50,
          metalness: 0.5,
        });

        const particle = new THREE.Mesh(geometry, material);
        particle.position.set(x, y, z);
        particleGroup.add(particle);
        particles.push(particle);
      }
    }

    let frame = 0;
    const animate = () => {
      requestAnimationFrame(animate);

      particles.forEach((p) => {
        const waveX = Math.sin(p.position.x / 8 - frame) * 2;
        const waveZ = Math.sin(p.position.z / 8 - frame) * 2;

        // Mise à jour de la position Y de chaque particule
        p.position.y = Math.min(waveX + waveZ, maxHeight);

        // Calcul de la hauteur normalisée pour la couleur
        const normalizedHeight = (p.position.y + maxHeight) / (2 * maxHeight);
        let color;

        // Interpolation des couleurs en fonction de la hauteur normalisée
        if (normalizedHeight < 0.5) {
          // Étendre la plage violette (50% du spectre)
          color = new THREE.Color().lerpColors(
            new THREE.Color(0x3b0077), // Violet foncé
            new THREE.Color(0x7b33ff), // Violet clair
            normalizedHeight * 2 // Échelle ajustée pour 50% de violet
          );
        } else if (normalizedHeight < 0.8) {
          // Réduire la plage bleue (30% du spectre)
          color = new THREE.Color().lerpColors(
            new THREE.Color(0x7b33ff), // Violet clair
            new THREE.Color(0x00aaff), // Bleu clair
            (normalizedHeight - 0.5) * 3.33 // Plage plus courte pour le bleu
          );
        } else {
          // Transition rapide vers le blanc (20% du spectre)
          color = new THREE.Color().lerpColors(
            new THREE.Color(0x00aaff), // Bleu clair
            new THREE.Color(0xffffff), // Blanc
            (normalizedHeight - 0.8) * 5 // Accentuation rapide
          );
        }

        // Applique la couleur calculée à chaque particule
        const material = p.material;
        material.color.set(color);
        material.emissive.set(color);
      });

      frame += 0.006;
      renderer.render(scene, camera); // Rendu de la scène sans post-traitement
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeRenderer);
      while (scene.children.length > 0) {
        scene.remove(scene.children[0]);
      }
    };
  }, []);

  return (
    <canvas
      id="bg"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 0,
        width: "100%",
        height: "100%",
      }}
    ></canvas>
  );
};

export default GradientWave;
